import { Clear, Today } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Divider,
  GridLegacy as Grid,
  TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers-pro';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { Controller, UseFormReturn, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { CostCenter } from '~/data/costCenters';
import { useEmploymentData } from '~/data/employee';
import { EnumListKey } from '~/data/enums';
import { TEXT_MAX_LENGTH } from '~/features/OverlappingEmploymentDialog/data';
import { resetEnumsFormValues } from '~/features/OverlappingEmploymentDialog/helpers';
import { useEmployment } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/Employment/useEmployment';
import { convertToMomentOrNull } from '~/utils/helpers/date';

import { useEnumFilteredOptions } from '../../hooks/useEnumFilteredOption';
import {
  OverlappingEmpForm,
  OverlappingEmploymentFormValues,
} from '../../hooks/useOverlappingEmploymentForm';
import { useSxStyles } from '../../useStyles';

interface Props {
  empId: number;
  formValues: OverlappingEmploymentFormValues;
}

export const BasicDataTabForm = ({ empId, formValues }: Props) => {
  const { t } = useTranslation();
  const sxStyles = useSxStyles();
  const {
    formProps: { control, getValues, setValue },
    helperValues: {
      legalEntities,
      orgTree,
      managers,
      allEmployees,
      allEnums,
      currencies,
      costCenterValues,
    },
  } = formValues;
  const [filteredOptions, filteredOptionsEmplStatus, filteredOptionsPosition] =
    useEnumFilteredOptions(control, [
      EnumListKey.EMPLOYMENT_TYPE,
      EnumListKey.EMPLOYMENT_STATUS,
      EnumListKey.EMPLOYEE_POSITION,
    ]);

  const [
    endOfEmploymentField,
    fEmplTrialPeriod,
    fEmplDateStart,
    fEmpLegalEntityValue,
    fInternalPositionUnitValue,
    fEmpCostCenter,
  ] = useWatch({
    control,
    name: [
      'overlappingEmployment.basicData.fEmplLastDateEmployment',
      'overlappingEmployment.basicData.fEmplTrialPeriod',
      'overlappingEmployment.basicData.fEmplDateStart',
      'overlappingEmployment.basicData.fEmpLegalEntity',
      'overlappingEmployment.basicData.fEmpBusinessUnit1',
      'overlappingEmployment.basicData.fEmpCostCenter',
    ],
  });

  const {
    data: [employmentData],
  } = useEmploymentData({ employeeId: empId });
  const { terminationReasons, fixedTermReasons, trialPeriodOptions } =
    useEmployment(employmentData);
  const trialPeriod = trialPeriodOptions.find(
    (option) => option.code === employmentData?.fEmplTrialPeriod,
  );

  const requiredText = t('fieldIsRequired');
  const dateFormat = 'YYYY-MM-DD';

  return (
    <>
      <Box sx={sxStyles.container}>
        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmpLegalEntity'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              placeholder={' '}
              options={legalEntities}
              getOptionLabel={(c) =>
                `${c.fLegalEntityName} (${c.fLegalEntityCode})`
              }
              isOptionEqualToValue={(o, v) =>
                o.fLegalEntityId === v.fLegalEntityId
              }
              value={
                legalEntities.find(
                  (entity) => `${entity.fLegalEntityId}` === value,
                ) || null
              }
              onChange={(e, value) => {
                const fLegalEntityId = value?.fLegalEntityId.toString() || '';
                onChange(fLegalEntityId);
                resetEnumsFormValues(
                  fLegalEntityId,
                  +getValues(
                    'overlappingEmployment.basicData.fEmpBusinessUnit1',
                  ),
                  allEnums,
                  { getValues, setValue } as UseFormReturn<OverlappingEmpForm>,
                  legalEntities,
                );
              }}
              renderInput={(params) => (
                <TextField
                  required
                  {...params}
                  label={t('fEmpLegalEntity')}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          rules={{ required: requiredText }}
          name="overlappingEmployment.basicData.fEmpBusinessUnit1"
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={orgTree[0].units}
              getOptionLabel={(o) => o.fTreeUnitName}
              isOptionEqualToValue={(o, v) => o.fTreeUnitId === v.fTreeUnitId}
              onChange={(_, value) => {
                onChange(value?.fTreeUnitId);
                setValue(
                  'overlappingEmployment.basicData.fInternalPositionManager',
                  managers
                    ?.find(
                      (manager) => String(manager) === value?.fTreeManagerId,
                    )
                    ?.toString() ?? '',
                );
              }}
              value={
                orgTree[0].units.find((o) => o.fTreeUnitId === value) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('fEmpBusinessUnits')}
                  value={value}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
            />
          )}
        />

        <Controller
          control={control}
          rules={{
            required: requiredText,
            validate: (value) => {
              // Is valid employee Id
              const num = parseInt(value.toString());
              return !isNaN(num) && num > 0;
            },
          }}
          name={'overlappingEmployment.basicData.fInternalPositionManager'}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              placeholder={''}
              options={managers ?? ([] as number[])}
              getOptionLabel={(c) =>
                `${allEmployees[c]?.fEmpLastName || ''}, ${
                  allEmployees[c]?.fEmpFirstName || ''
                }`
              }
              isOptionEqualToValue={(o, v) => o === v}
              onChange={(e, value) => {
                onChange(value?.toString());
              }}
              value={managers?.find((mg) => mg.toString() === value) || null}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('laManager')}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplType'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              placeholder={''}
              options={filteredOptions}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              getOptionLabel={(c) => c.name}
              value={filteredOptions.find((val) => val.code === value) || null}
              onChange={(e, value) => {
                onChange(value?.code);
              }}
              disabled={!fEmpLegalEntityValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('laEmploymentType')}
                  error={!!error}
                  helperText={
                    error?.message ||
                    (!fEmpLegalEntityValue ? t('laSelectFirstLegEnt') : '')
                  }
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplDetailStatus'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={filteredOptionsEmplStatus}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code || '')}
              value={
                filteredOptionsEmplStatus.find((o) => o?.code === value) || null
              }
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              disabled={!fEmpLegalEntityValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('laEmploymentStatus')}
                  error={!!error}
                  helperText={
                    error?.message ||
                    (!fEmpLegalEntityValue ? t('laSelectFirstLegEnt') : '')
                  }
                />
              )}
            />
          )}
        />

        <Grid
          justifyContent="flex-start"
          alignItems="center"
          display="flex"
          item
          container
        >
          <Controller
            name={'overlappingEmployment.basicData.fEmplDateStart'}
            control={control}
            rules={{ required: requiredText }}
            render={({ field: { onChange, value }, fieldState: { error } }) => (
              <DatePicker
                value={convertToMomentOrNull(value)}
                onChange={(v: Moment | null) => {
                  if (v) {
                    const empTrialPeriodValue = getValues(
                      'overlappingEmployment.basicData.fEmplTrialPeriod',
                    );
                    if (empTrialPeriodValue) {
                      setValue(
                        'overlappingEmployment.basicData.fEmplTrialPeriodEndDate',
                        moment(v)
                          .add(
                            parseInt(empTrialPeriodValue.charAt(1)),
                            'months',
                          )
                          .subtract(1, 'days')
                          .format(dateFormat),
                      );
                    } else {
                      setValue(
                        'overlappingEmployment.basicData.fEmplTrialPeriodEndDate',
                        null,
                      );
                    }
                    setValue(
                      'overlappingEmployment.employmentDetails.fEmplDetailStartDate',
                      v.format(dateFormat),
                    );
                  }
                  setValue(
                    'overlappingEmployment.basicData.fEmplDateArrival',
                    v?.format(dateFormat) || null,
                  );
                  onChange(v?.format(dateFormat));
                }}
                label={t('fEmplDateStart')}
                slotProps={{
                  textField: {
                    required: true,
                    error: !!error,
                    helperText: error?.message,
                    sx: {
                      ...sxStyles.shortDate,
                      ...sxStyles.inputBottomMargin,
                    },
                  },
                }}
              />
            )}
          />
          <Button
            onClick={() => {
              setValue(
                'overlappingEmployment.basicData.fEmplDateStart',
                moment(new Date()).format(dateFormat),
              );
              setValue(
                'overlappingEmployment.basicData.fEmplDateArrival',
                moment(new Date()).format(dateFormat),
              );
            }}
            startIcon={<Today />}
            sx={sxStyles.clearBtn}
          >
            {t('laToday')}
          </Button>
        </Grid>

        <Controller
          name={'overlappingEmployment.basicData.fEmplDateArrival'}
          control={control}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              value={convertToMomentOrNull(value)}
              onChange={(v: Moment | null) => {
                onChange(v?.format(dateFormat));
              }}
              label={t('fArrivalDate')}
              slotProps={{
                textField: {
                  required: true,
                  error: !!error,
                  helperText: error?.message,
                  sx: {
                    ...sxStyles.shortDate,
                    ...sxStyles.inputBottomMargin,
                  },
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplTrialPeriod'}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              options={trialPeriodOptions}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.code === v.code}
              onChange={(_, value) => {
                onChange(value?.code);
                if (value?.code) {
                  setValue(
                    'overlappingEmployment.basicData.fEmplTrialPeriodEndDate',
                    moment(fEmplDateStart)
                      .add(parseInt(value?.code?.charAt(1)), 'months')
                      .subtract(1, 'days')
                      .format(dateFormat),
                  );
                }
              }}
              defaultValue={trialPeriod}
              value={trialPeriodOptions.find((o) => o.code === value) || null}
              renderInput={(params) => (
                <TextField {...params} label={t('fTrialPeriod')} />
              )}
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
            />
          )}
        />

        <Controller
          name={'overlappingEmployment.basicData.fEmplTrialPeriodEndDate'}
          control={control}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <DatePicker
              label={t('laTrialPeriodEndDate')}
              onChange={(v: Moment | null) => onChange(v?.format(dateFormat))}
              value={convertToMomentOrNull(value)}
              slotProps={{
                textField: {
                  required: !!fEmplTrialPeriod,
                  error: !!error,
                  helperText: error?.message,
                  sx: {
                    ...sxStyles.dateInput,
                    ...sxStyles.inputBottomMargin,
                  },
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplFixedTermReason'}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              options={fixedTermReasons}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code)}
              value={fixedTermReasons.find((o) => o?.code === value) || null}
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              renderInput={(params) => (
                <TextField {...params} label={t('fReasonFixTerm')} />
              )}
            />
          )}
        />

        <Controller
          name={'overlappingEmployment.basicData.fEmplPlannedEndDate'}
          control={control}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label={t('fEmplPlannedEndDate')}
              onChange={(v: Moment | null) => onChange(v?.format(dateFormat))}
              value={convertToMomentOrNull(value)}
              minDate={
                moment(
                  getValues('overlappingEmployment.basicData.fEmplDateStart'),
                ) || undefined
              }
              slotProps={{
                textField: {
                  sx: {
                    ...sxStyles.dateInput,
                    ...sxStyles.inputBottomMargin,
                  },
                },
              }}
            />
          )}
        />

        <Grid
          justifyContent="flex-start"
          alignItems="center"
          display="flex"
          item
          container
        >
          <Controller
            name={'overlappingEmployment.basicData.fEmplLastDateEmployment'}
            control={control}
            render={({ field: { onChange, value } }) => (
              <DatePicker
                label={t('fLastDEmpl')}
                onChange={(v: Moment | null) => {
                  onChange(v?.format(dateFormat));
                  setValue(
                    'overlappingEmployment.basicData.fEmplTerminationDate',
                    v?.format(dateFormat) || null,
                  );
                }}
                value={convertToMomentOrNull(value)}
                slotProps={{
                  textField: {
                    sx: {
                      ...sxStyles.shortDate,
                      ...sxStyles.inputBottomMargin,
                    },
                  },
                }}
              />
            )}
          />
          <Button
            onClick={() => {
              setValue(
                'overlappingEmployment.basicData.fEmplLastDateEmployment',
                null,
                { shouldDirty: true },
              );
              setValue(
                'overlappingEmployment.basicData.fEmplTerminationDate',
                null,
                {
                  shouldDirty: true,
                },
              );
              setValue(
                'overlappingEmployment.basicData.fEmplTerminationReason',
                '',
                {
                  shouldDirty: true,
                },
              );
            }}
            startIcon={<Clear />}
            sx={sxStyles.clearBtn}
          >
            {t('laClear')}
          </Button>
        </Grid>

        <Controller
          name={'overlappingEmployment.basicData.fEmplTerminationDate'}
          control={control}
          rules={{ required: !!endOfEmploymentField }}
          render={({ field: { onChange, value } }) => (
            <DatePicker
              label={t('fLastDTemp')}
              onChange={(v: Moment | null) => onChange(v?.format(dateFormat))}
              value={convertToMomentOrNull(value)}
              minDate={
                moment(
                  getValues('overlappingEmployment.basicData.fEmplDateStart'),
                ) || undefined
              }
              maxDate={
                endOfEmploymentField ? moment(endOfEmploymentField) : undefined
              }
              slotProps={{
                textField: {
                  required: !!endOfEmploymentField,
                  sx: {
                    ...sxStyles.shortDate,
                    ...sxStyles.inputBottomMargin,
                  },
                },
              }}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplTerminationReason'}
          rules={{
            required: !!endOfEmploymentField && requiredText,
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              options={terminationReasons}
              getOptionLabel={(o) => o?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(_, value) => onChange(value?.code)}
              value={terminationReasons.find((o) => o?.code === value) || null}
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              disabled={!fEmpLegalEntityValue}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required={!!endOfEmploymentField}
                  label={t('laTerminationReason')}
                  error={!!error}
                  helperText={
                    error?.message ||
                    (!fEmpLegalEntityValue ? t('laSelectFirstLegEnt') : '')
                  }
                />
              )}
            />
          )}
        />
      </Box>

      <Divider sx={sxStyles.divider} />

      <Box sx={sxStyles.container}>
        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmplCurrency'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              placeholder={''}
              options={currencies}
              value={value}
              onChange={(e, value) => {
                onChange(value);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('fCurrency')}
                  error={!!error}
                  helperText={error?.message}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fInternalPositionPosition'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              placeholder={t('laSelectFirstLegEnt')}
              disabled={!fEmpLegalEntityValue}
              options={filteredOptionsPosition}
              getOptionLabel={(c) => c?.name || ''}
              isOptionEqualToValue={(o, v) => o?.code === v?.code}
              onChange={(e, value) => {
                onChange(value?.code || '');
              }}
              value={
                filteredOptionsPosition.find((ep) => ep?.code === value) || null
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('laPosition')}
                  error={!!error}
                  helperText={
                    error?.message ||
                    (!fEmpLegalEntityValue ? t('laSelectFirstLegEnt') : '')
                  }
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmpJobTitle'}
          rules={{
            maxLength: {
              value: TEXT_MAX_LENGTH,
              message: t('laMaxLengthRequired'),
            },
          }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <TextField
              value={value}
              sx={{
                ...sxStyles.selectStyle,
                ...sxStyles.inputBottomMargin,
              }}
              onChange={onChange}
              label={t('fEmpJobTitle')}
              helperText={error?.message}
              error={!!error}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmpCostCenter'}
          rules={{ required: requiredText }}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              placeholder={
                !fInternalPositionUnitValue ? t('laSelectFirstOrgUnit') : ' '
              }
              options={costCenterValues}
              getOptionLabel={(c) =>
                `${c.fCostCenterCode}, ${c.fCostCenterName}`
              }
              isOptionEqualToValue={(o, v) =>
                o?.fCostCenterId === v?.fCostCenterId
              }
              value={
                costCenterValues.find(
                  (center) => center?.fCostCenterId?.toString() === value,
                ) || null
              }
              disabled={!fEmpLegalEntityValue}
              onChange={(e, value) => {
                onChange(value?.fCostCenterId?.toString());
                const additionalCC = getValues(
                  'overlappingEmployment.basicData.fEmpAddlCostCenters',
                );
                if (additionalCC?.length) {
                  setValue(
                    'overlappingEmployment.basicData.fEmpAddlCostCenters',
                    (additionalCC as CostCenter[]).filter(
                      (cc) => cc.fCostCenterId !== value?.fCostCenterId,
                    ),
                  );
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  required
                  label={t('fEmpCostCenter')}
                  error={!!error}
                  helperText={
                    error?.message ||
                    (!fEmpLegalEntityValue ? t('laSelectFirstLegEnt') : '')
                  }
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name={'overlappingEmployment.basicData.fEmpAddlCostCenters'}
          render={({ field: { onChange, value } }) => (
            <Autocomplete
              sx={{ ...sxStyles.selectStyle, ...sxStyles.inputBottomMargin }}
              placeholder={' '}
              options={costCenterValues}
              getOptionLabel={(c) =>
                `${c.fCostCenterCode}, ${c.fCostCenterName}`
              }
              isOptionEqualToValue={(o, v) =>
                o?.fCostCenterId === v?.fCostCenterId
              }
              multiple
              value={value ?? []}
              onChange={(e, value) => {
                onChange(value);
              }}
              getOptionDisabled={(option) =>
                `${option.fCostCenterId}` === fEmpCostCenter
              }
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option.fCostCenterCode}, ${option.fCostCenterName}`}
                    {...getTagProps({ index })}
                  />
                ))
              }
              renderInput={(params) => (
                <TextField {...params} label={t('fEmpAddlCostCenters')} />
              )}
            />
          )}
        />
      </Box>
    </>
  );
};
